import * as THREE from "three";
import { findJointInMap } from "../../stores/sceneActions/jointActions";
import { getSceneState } from "../../stores/sceneStore";
import { logger } from "../../utils/logger";
import { createJointVisualization } from "../jointVisualization";

const devLog = () => {};
const HINGE_BASIS_HINT_EPSILON = 1e-8;
const HINGE_BASIS_DOMINANT_AXIS_RATIO = 4;

function toFiniteVector3(value) {
  if (!Array.isArray(value) || value.length !== 3) return null;
  const vector = new THREE.Vector3(Number(value[0]), Number(value[1]), Number(value[2]));
  return Number.isFinite(vector.x) && Number.isFinite(vector.y) && Number.isFinite(vector.z)
    ? vector
    : null;
}

function toQuaternion(value) {
  if (!Array.isArray(value) || value.length !== 4) return new THREE.Quaternion();
  const quaternion = new THREE.Quaternion(
    Number(value[1]),
    Number(value[2]),
    Number(value[3]),
    Number(value[0])
  );
  return Number.isFinite(quaternion.x) &&
    Number.isFinite(quaternion.y) &&
    Number.isFinite(quaternion.z) &&
    Number.isFinite(quaternion.w)
    ? quaternion.normalize()
    : new THREE.Quaternion();
}

function getProjectedHingeVector(candidate, jointPosition, jointAxis) {
  const relative = candidate.clone().sub(jointPosition);
  const projected = relative.sub(jointAxis.clone().multiplyScalar(relative.dot(jointAxis)));
  return projected.lengthSq() > HINGE_BASIS_HINT_EPSILON ? projected : null;
}

function alignDominantAxis(vector) {
  const components = [
    { axis: "x", value: Math.abs(vector.x) },
    { axis: "y", value: Math.abs(vector.y) },
    { axis: "z", value: Math.abs(vector.z) },
  ].sort((a, b) => b.value - a.value);
  if (
    components[0].value > HINGE_BASIS_HINT_EPSILON &&
    components[0].value >= components[1].value * HINGE_BASIS_DOMINANT_AXIS_RATIO
  ) {
    return components[0].axis === "x"
      ? new THREE.Vector3(vector.x, 0, 0)
      : components[0].axis === "y"
        ? new THREE.Vector3(0, vector.y, 0)
        : new THREE.Vector3(0, 0, vector.z);
  }

  return vector.clone();
}

function collectDescendantHingeBasisCandidates(
  bodyNode,
  jointPosition,
  jointAxis,
  parentPos,
  parentQuat,
  candidates
) {
  for (const child of Array.isArray(bodyNode?.children) ? bodyNode.children : []) {
    if (!child || typeof child === "string") continue;
    const childLocalPos = toFiniteVector3(child.position) || new THREE.Vector3();
    const childLocalQuat = toQuaternion(child.orientation);
    const childPos = parentPos.clone().add(childLocalPos.clone().applyQuaternion(parentQuat));
    const childQuat = parentQuat.clone().multiply(childLocalQuat);
    const projectedChild = getProjectedHingeVector(childPos, jointPosition, jointAxis);
    if (projectedChild) candidates.push(projectedChild);

    for (const geom of Array.isArray(child.geoms) ? child.geoms : []) {
      const geomLocalPos = toFiniteVector3(geom?.position);
      if (!geomLocalPos) continue;
      const geomPos = childPos.clone().add(geomLocalPos.applyQuaternion(childQuat));
      const projectedGeom = getProjectedHingeVector(geomPos, jointPosition, jointAxis);
      if (projectedGeom) candidates.push(projectedGeom);
    }

    collectDescendantHingeBasisCandidates(
      child,
      jointPosition,
      jointAxis,
      childPos,
      childQuat,
      candidates
    );
  }
}

function getFarthestCandidate(candidates) {
  let farthest = null;
  let farthestLengthSq = 0;
  candidates.forEach((candidate) => {
    const lengthSq = candidate.lengthSq();
    if (lengthSq > farthestLengthSq) {
      farthest = candidate;
      farthestLengthSq = lengthSq;
    }
  });
  return farthest;
}

function getPrincipalHingeDirection(candidates) {
  const farthest = getFarthestCandidate(candidates);
  if (!farthest) return null;
  if (candidates.length === 1) return alignDominantAxis(farthest).toArray();

  let xx = 0;
  let xy = 0;
  let xz = 0;
  let yy = 0;
  let yz = 0;
  let zz = 0;
  candidates.forEach((candidate) => {
    xx += candidate.x * candidate.x;
    xy += candidate.x * candidate.y;
    xz += candidate.x * candidate.z;
    yy += candidate.y * candidate.y;
    yz += candidate.y * candidate.z;
    zz += candidate.z * candidate.z;
  });

  let direction = farthest.clone().normalize();
  for (let i = 0; i < 8; i++) {
    const next = new THREE.Vector3(
      xx * direction.x + xy * direction.y + xz * direction.z,
      xy * direction.x + yy * direction.y + yz * direction.z,
      xz * direction.x + yz * direction.y + zz * direction.z
    );
    if (next.lengthSq() <= HINGE_BASIS_HINT_EPSILON) break;
    direction = next.normalize();
  }

  if (direction.dot(farthest) < 0) direction.negate();
  let magnitude = 0;
  candidates.forEach((candidate) => {
    magnitude = Math.max(magnitude, Math.abs(candidate.dot(direction)));
  });
  if (magnitude <= HINGE_BASIS_HINT_EPSILON) return alignDominantAxis(farthest).toArray();
  return alignDominantAxis(direction.multiplyScalar(magnitude)).toArray();
}

export function getHingeVisualizationBasisHint(bodyNode, joint) {
  if (String(joint?.kind || "").toLowerCase() !== "hinge") return null;
  const jointPosition = toFiniteVector3(joint?.position) || new THREE.Vector3();
  const axis = toFiniteVector3(joint?.axis) || new THREE.Vector3(0, 0, 1);
  if (axis.lengthSq() <= HINGE_BASIS_HINT_EPSILON) return null;
  axis.normalize();

  const candidates = [];
  collectDescendantHingeBasisCandidates(
    bodyNode,
    jointPosition,
    axis,
    new THREE.Vector3(),
    new THREE.Quaternion(),
    candidates
  );
  const descendantHint = getPrincipalHingeDirection(candidates);
  if (descendantHint) return descendantHint;

  for (const geom of Array.isArray(bodyNode?.geoms) ? bodyNode.geoms : []) {
    const geomPos = toFiniteVector3(geom?.position);
    if (!geomPos) continue;
    const hint = getProjectedHingeVector(geomPos, jointPosition, axis);
    if (hint) return alignDominantAxis(hint).toArray();
  }

  const originHint = getProjectedHingeVector(new THREE.Vector3(), jointPosition, axis);
  return originHint ? alignDominantAxis(originHint).toArray() : null;
}

export function attachBodyJoints({
  bodyNode,
  group,
  poseGroup,
  parentRot,
  parentPos,
  accumulatedRot,
  sceneActions,
  jointObjectsRef,
  showJoints,
  jointMarkerSizes,
}) {
  // Add joints
  if (bodyNode.name) {
    const state = getSceneState();
    const jointMap = state.joints;

    const resolveJoint = (entry) => {
      if (!entry) return null;

      if (typeof entry === "string") {
        const found = findJointInMap(jointMap, entry);
        if (!found) {
          devLog(
            `[processBodyFromStore] Joint "${entry}" not found in jointMap (keys: ${Object.keys(jointMap).join(", ")})`
          );
        }
        return found;
      }

      const { body: _ignoredBody, ...rest } = entry;
      // Direct UUID access - no fallbacks
      const base = entry.uuid ? jointMap[entry.uuid] : null;

      if (!base) {
        devLog(
          `[processBodyFromStore] Joint UUID "${entry.uuid}" not found in jointMap (keys: ${Object.keys(jointMap).join(", ")})`
        );
        // If not found in map, use the joint object itself
        return rest;
      }
      // Prefer canonical map values so live joint edits win over stale embedded refs.
      return { ...rest, ...base };
    };

    const resolvedBodyJoints = (Array.isArray(bodyNode.joints) ? bodyNode.joints : [])
      .map(resolveJoint)
      .filter(Boolean);

    // Debug logging
    if (resolvedBodyJoints.length > 0 || bodyNode.joints?.length > 0) {
      devLog(
        `[processBodyFromStore] Body ${bodyNode.name} has ${resolvedBodyJoints.length} resolved joints, ${bodyNode.joints?.length || 0} raw joints`
      );
    }

    resolvedBodyJoints.forEach((bodyJoint) => {
      if (!bodyJoint) return;

      // Normalize joint API: ensure joint.range is populated before visualization/kinematics
      try {
        if (!Array.isArray(bodyJoint.range) || bodyJoint.range.length !== 2) {
          // Get joint range directly from state
          const jointFromState = bodyJoint.uuid ? state.joints[bodyJoint.uuid] : null;
          if (jointFromState) {
            const isScalarJoint =
              jointFromState.kind === "hinge" || jointFromState.kind === "slide";
            if (isScalarJoint && jointFromState.range) {
              bodyJoint.range = jointFromState.range;
            }
          }
          if (!Array.isArray(bodyJoint.range) || bodyJoint.range.length !== 2) {
            // Fallback for hinge/slide when range not available from state
            const kind = String(bodyJoint.kind || "").toLowerCase();
            bodyJoint.range = kind === "slide" ? [0, 1] : [-Math.PI, Math.PI];
          }
        }
      } catch (error) {
        logger.warn("[processBodyFromStore] Unexpected error while processing body", error);
      }

      // Focus: joint placement
      try {
        const jointKey = bodyJoint.uuid || bodyJoint.name;
        if (!jointKey) {
          logger.warn(
            `[processBodyFromStore] Unable to register joint visualization for body ${bodyNode.name} - missing joint uuid and name`
          );
          return;
        }
        jointObjectsRef.current[jointKey] = {
          bodyGroup: poseGroup,
          initialRot: poseGroup.quaternion.clone(),
          initialPos: poseGroup.position.clone(),
          parentRot: parentRot.clone(), // Use parent rotation only for joint axis transformation
          parentPos: parentPos.clone(),
          joint: bodyJoint,
          jointUuid: jointKey,
          jointName: bodyJoint.name,
        };
        try {
          // Debug: record joint registration details for diagnosis
          const jr = jointObjectsRef.current[jointKey];
          const pr = jr.parentRot || new THREE.Quaternion();
          const ir = jr.initialRot || new THREE.Quaternion();
          const jAxis = Array.isArray(bodyJoint.axis) ? bodyJoint.axis : null;
          devLog(
            "[Kinematics.registerJoint]",
            bodyJoint.name,
            "parentRot(qw,qx,qy,qz)=",
            `[${[pr.w, pr.x, pr.y, pr.z].map((n) => Number(n).toFixed(4)).join(", ")}]`,
            "initialRot(qw,qx,qy,qz)=",
            `[${[ir.w, ir.x, ir.y, ir.z].map((n) => Number(n).toFixed(4)).join(", ")}]`,
            "axis=",
            jAxis ? `[${jAxis.map((n) => Number(n).toFixed(4)).join(", ")}]` : "(unset)"
          );
        } catch (error) {
          logger.warn("[processBodyFromStore] Unexpected error while processing body", error);
        }

        // Create joint visualization
        // Joint visualization uses axis from jointInfo, not a separate quaternion
        // Pass identity quaternion since bodyQuat parameter is unused in createJointVisualization
        const jointViz = createJointVisualization(
          bodyJoint,
          accumulatedRot.clone(),
          new THREE.Quaternion(), // Identity quaternion - not used, joint axis is used instead
          group.position.clone(),
          showJoints,
          jointMarkerSizes,
          sceneActions,
          { basisHint: getHingeVisualizationBasisHint(bodyNode, bodyJoint) }
        );
        if (jointViz) {
          jointViz.userData.bodyUuid = bodyNode.uuid;
          jointViz.userData.bodyName = bodyNode.name || "";
          devLog(
            `[processBodyFromStore] Created joint viz for ${bodyJoint.name}, visible: ${showJoints}, position:`,
            jointViz.position.toArray()
          );
          // STABILITY CONTRACT (do not regress without an explicit design pass):
          // Joint range arcs and min/max markers must stay in the body container frame
          // (`group`), not under `poseGroup`, so they do not inherit live joint / pivot
          // motion. `jointViz.position` / `quaternion` encode the q=0 joint frame in
          // parent space. See joint UX discussion - changing this breaks limit viz again.
          const jointLocalPos = Array.isArray(bodyJoint?.position) ? bodyJoint.position : [0, 0, 0];
          const jointOffsetParent = new THREE.Vector3(...jointLocalPos).applyQuaternion(
            poseGroup.quaternion
          );
          jointViz.position.copy(poseGroup.position.clone().add(jointOffsetParent));
          jointViz.quaternion.copy(poseGroup.quaternion);
          group.add(jointViz);
          jointObjectsRef.current[jointKey].jointVizGroup = jointViz;
        }
      } catch (error) {
        logger.error(`[Scene] Error creating joint object for ${bodyJoint.name}:`, error);
      }
    });
  }
}
